import { OnThisPageItem } from '../types/onThisPage';

export function flattenTocIds(items: OnThisPageItem[]): string[] {
  return items.flatMap((item) => [
    item.id,
    ...(item.children ? flattenTocIds(item.children) : []),
  ]);
}

function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
}

function uniqueId(base: string, used: Set<string>): string {
  let id = base;
  let n = 2;
  while (used.has(id)) {
    id = `${base}-${n}`;
    n += 1;
  }
  used.add(id);
  return id;
}

/** Stable URL hash for a section title. Pass sections in page order so duplicates stay unique. */
export function sectionAnchors(items: Array<{ id: string; title: string }>): Map<string, string> {
  const used = new Set<string>();
  const anchors = new Map<string, string>();
  for (const item of items) {
    anchors.set(item.id, uniqueId(slugifyHeading(item.title), used));
  }
  return anchors;
}

function nestHeadingToc(
  headings: Array<{ id: string; label: string; level: number }>
): OnThisPageItem[] {
  const root: OnThisPageItem[] = [];
  const stack: Array<{ level: number; item: OnThisPageItem }> = [];

  for (const heading of headings) {
    const item: OnThisPageItem = { id: heading.id, label: heading.label };
    while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
      stack.pop();
    }
    if (stack.length === 0) {
      root.push(item);
    } else {
      const parent = stack[stack.length - 1].item;
      parent.children = parent.children ?? [];
      parent.children.push(item);
    }
    stack.push({ level: heading.level, item });
  }

  return root;
}

/** Assign ids to headings in the container and return a nested TOC. */
export function applyHeadingAnchors(container: HTMLElement): OnThisPageItem[] {
  const nodes = container.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6');
  const used = new Set<string>();
  const headings: Array<{ id: string; label: string; level: number }> = [];

  nodes.forEach((el) => {
    const label = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label) return;
    const level = Number(el.tagName.slice(1));
    const existing = el.getAttribute('id')?.trim() || '';
    const id =
      existing && !used.has(existing) ? uniqueId(existing, used) : uniqueId(slugifyHeading(label), used);
    el.id = id;
    el.classList.add('scroll-mt-24');
    headings.push({ id, label, level });
  });

  return nestHeadingToc(headings);
}
